import React, { useEffect, useRef, useState, useContext } from 'react';
import { Map as AtlasMap } from 'azure-maps-control';
import 'azure-maps-control/dist/atlas.min.css';
import { getMapOptions } from 'components/maps/azure/AzureMapsShared';
import AuthContext from 'context/AuthContext';


const AzureMapBase = ({
    center: centerProp = [0, 0],
    zoom: zoomProp = 13,
    height = '300px',
    onMapReady,
    subscriptionKey,
    children
}) => {
    const { panel, azureMapsKey } = useContext(AuthContext);
    const mapContainerRef = useRef(null);
    const [map, setMap] = useState(null);
    const isInitializing = useRef(false);

    // Helper to validate and normalize center coordinates
    const isValidCoord = (c) => Array.isArray(c) && c.length >= 2 && typeof c[0] === 'number' && typeof c[1] === 'number' && !isNaN(c[0]) && !isNaN(c[1]);
    const center = isValidCoord(centerProp) ? centerProp : [0, 0];
    const zoom = typeof zoomProp === 'number' && !isNaN(zoomProp) ? zoomProp : 13;

    useEffect(() => {
        if (!mapContainerRef.current || isInitializing.current) return;

        const effectiveKey = subscriptionKey || panel?.azure_maps_key || azureMapsKey;
        if (!effectiveKey) {
            console.warn("AzureMapBase: No API key available yet.");
            return;
        }

        isInitializing.current = true;
        let mapInstance = null;

        try {
            // Initialize map
            mapInstance = new AtlasMap(mapContainerRef.current, {
                ...getMapOptions(effectiveKey),
                center: [center[1], center[0]], // Azure Maps uses [lng, lat]
                zoom: zoom,
                view: 'Auto'
            });

            mapInstance.events.add('ready', () => {
                setMap(mapInstance);
                if (onMapReady) {
                    onMapReady(mapInstance);
                }
                isInitializing.current = false;
            });
        } catch (e) {
            console.error("AzureMapBase initialization error:", e);
            isInitializing.current = false;
        }

        return () => {
            if (mapInstance) {
                try {
                    mapInstance.dispose();
                } catch (e) {
                    console.warn("AzureMapBase disposal warning:", e);
                }
                setMap(null);
                isInitializing.current = false;
            }
        };
    }, [azureMapsKey, panel?.azure_maps_key, subscriptionKey]);

    // Update center if it changes
    useEffect(() => {
        if (map && isValidCoord(centerProp)) {
            map.setCamera({
                center: [centerProp[1], centerProp[0]],
                type: 'jump'
            });
        }
    }, [centerProp, map]);

    return (
        <div
            ref={mapContainerRef}
            style={{ width: '100%', height: height, position: 'relative' }}
        >
            {map && children && React.Children.map(children, child => {
                if (React.isValidElement(child)) {
                    return React.cloneElement(child, { map });
                }
                return child;
            })}
        </div>
    );
};

export default AzureMapBase;
